import {
  DataBlockTypeDto,
  DataType,
  DocumentQueryDto,
  DocumentQueryFieldDto,
  MergeFieldQueryType,
} from '@lmt/shared-types';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { camelCase, upperFirst } from 'lodash';

export function convertToJsDataType(
  dataType: DataType,
): 'string' | 'number' | 'Date' | 'boolean' {
  switch (dataType) {
    case DataType.Boolean:
      return 'boolean';

    case DataType.DateOnly:
    case DataType.DateTime:
    case DataType.TimeOnly:
      return 'Date';

    case DataType.Number:
      return 'number';

    case DataType.Text:
    case DataType.MultilineText:
      return 'string';

    case DataType.Dictionary:
    case DataType.Table:
      return 'string';

    case DataType.Undefined:
      return 'string';

    default:
      assertNever(dataType);
  }
}

export function getDocumentQueryFields(
  documentQueries: DocumentQueryDto[],
  type: MergeFieldQueryType,
  dataBlockTypeId?: number,
): DocumentQueryFieldDto[] {
  let queriesOfType = documentQueries.filter(
    (x) => x.queryIdentifier.queryType === type,
  );
  if (dataBlockTypeId) {
    queriesOfType = queriesOfType.filter(
      (x) => x.queryIdentifier.dataBlockTypeId === dataBlockTypeId,
    );
  }
  if (!queriesOfType?.length) {
    return [];
  }
  const result: DocumentQueryFieldDto[] = [];
  queriesOfType.forEach((item) => {
    const fields = item.queryFields.filter(
      (field) => field.dataField !== 'Limit',
    );
    result.push(...fields);
  });
  return result;
}

/**
 * Returns document query fields of type as string (e.g. 'id: number; name: string'
 */
function convertQueryFieldsToString(fields: DocumentQueryFieldDto[]) {
  return fields
    .map(
      (field) =>
        `${field.caption ? `/** ${field.caption} */\n` : ''}'${
          field.dataField
        }': ${
          field.nestedFields
            ? upperFirst(field.dataField) + 'Type'
            : convertToJsDataType(field.dataType)
        };`,
    )
    .join('\n');
}

/**
 * Get recursively nested fields declarations for query fields of entity (e.g. patient or visit)
 * e.g. patient's doctor has own fields, so we need type declaration for doctor
 * @param fields Document query fields of entity
 */
function getNestedFieldDeclarations(fields: DocumentQueryFieldDto[]): string {
  const result: string[] = [];
  fields.forEach((field) => {
    if (!field.nestedFields) {
      return;
    }
    result.push(
      `declare type ${
        upperFirst(field.dataField) + 'Type'
      } = {\n${convertQueryFieldsToString(field.nestedFields)}\n};`,
    );
    result.push(getNestedFieldDeclarations(field.nestedFields));
  });
  return result.join('\n');
}

/**
 * Returns function declarations with the same return type
 * e.g. 'declare type Patient = { id: number; name: string; doctor: DoctorType; };
 * declare type DoctorType = { id: number; specialty: string; };
 * declare function patient(): Patient;
 * declare function firstPatient(): Patient;'
 */
export function getFunctionDeclarations(
  functionNames: string[],
  functionReturnType: string,
  documentQueries?: DocumentQueryDto[],
  queryType?: MergeFieldQueryType,
): string {
  const result: string[] = [];
  if (documentQueries && queryType) {
    const fields = getDocumentQueryFields(documentQueries, queryType);
    result.push(
      `declare type ${functionReturnType} = {\n${convertQueryFieldsToString(
        fields,
      )}\n};`,
    );
    if (fields.length) {
      result.push(getNestedFieldDeclarations(fields));
    }
  }
  functionNames.forEach((functionName) => {
    result.push(`declare function ${functionName}(): ${functionReturnType};`);
  });
  return result.join('\n');
}

function getReturnTypeName(dataBlockType: DataBlockTypeDto) {
  // we need to put `Type` before the `dataBlockType.value`, because
  // `dataBlockType.value` might start with a number,
  // while type names can't start with a number
  return 'Type' + upperFirst(camelCase(dataBlockType.value));
}

/**
 * Returns data block function declarations for all data block types
 * e.g.
 * 'declare function block(type: 'MSLT'): MsltType;
 * 'declare function block(type: 'PSG'): PsgType;
 * 'declare function block(type: string): any;
 */
export function getDataBlockFunctionDeclarations(
  functionNames: string[],
  documentQueries: DocumentQueryDto[],
  dataBlockTypes: DataBlockTypeDto[],
  additionalParams?: string,
  ignoreTypeCreating?: boolean,
  returnsArray?: boolean,
) {
  const result: string[] = [];
  const dataBlockQueries = documentQueries.filter(
    (x) =>
      x.queryIdentifier.queryType === MergeFieldQueryType.DataBlockQuery &&
      x.queryIdentifier.dataBlockTypeId,
  );

  const declaredTypes: Record<string, DocumentQueryFieldDto[]> = {};

  if (!ignoreTypeCreating && !returnsArray) {
    dataBlockQueries.forEach((documentQuery) => {
      const dataBlockType = dataBlockTypes.find(
        (type) => type.id === documentQuery.queryIdentifier.dataBlockTypeId!,
      )!;
      if (!dataBlockType) return;
      const returnType = getReturnTypeName(dataBlockType);
      if (!declaredTypes[returnType]) {
        declaredTypes[returnType] = [];
      }
      const fields = getDocumentQueryFields(
        documentQueries,
        MergeFieldQueryType.DataBlockQuery,
        dataBlockType.id,
      );
      declaredTypes[returnType].push(...fields);
    });
  }

  dataBlockQueries.forEach((documentQuery) => {
    const dataBlockType = dataBlockTypes.find(
      (type) => type.id === documentQuery.queryIdentifier.dataBlockTypeId!,
    )!;
    if (!dataBlockType) return;
    const returnType =
      getReturnTypeName(dataBlockType) + (returnsArray ? '[]' : ''); // e.g. Night Protocol => NightProtocolType

    if (declaredTypes[returnType]) {
      result.push(
        `declare type ${returnType} = {\n${convertQueryFieldsToString(
          declaredTypes[returnType],
        )}\n};`,
      );
    }
    functionNames.forEach((functionName) =>
      result.push(
        `declare function ${functionName}(type: '${dataBlockType.value}'${
          additionalParams ? ',' + additionalParams : ''
        }): ${returnType};`,
      ),
    );
  });
  functionNames.forEach((functionName) => {
    result.push(`declare function ${functionName}(type: string): any;`);
  });
  return result.join('\n');
}
